import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import "./ActionTable.css";
import heartLoading from "../../assests/heartLoading.gif";
import IncidentPieChart from "./IncidentPieChart";
import IncidentTable from "./IncidentTable";
import { RotateCcw, Filter } from "lucide-react";
import SingleSelect from "./SingleSelect";

const formatIncidentCategory = (category = "") => {
  if (!category) return "";
  return category.trim().toLowerCase() === "results unavailable"
    ? "SLA Breach"
    : category;
};

const PAGE_SIZE = 10;

const ActionTable = () => {
  const [selectedParentDomain, setSelectedParentDomain] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedSubDomain, setSelectedSubDomain] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [searchValue, setSearchValue] = useState("");
  const [resolutionPendingStatus, setResolutionPendingStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [incidents, setIncidents] = useState([]);
  const [incidentCategories, setIncidentCategories] = useState([]);
  const [incidentCategoryCounts, setIncidentCategoryCounts] = useState({});
  const [filters, setFilters] = useState(null);

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
    totalCount: 0,
    totalPages: 0,
  });

  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [resetTrigger, setResetTrigger] = useState(false);

  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  const [columnSearch, setColumnSearch] = useState({
    snowId: "",
    incidentDescription: "",
    incidentCategory: "",
    assignedTo: "",
    status: "",
    resolutionPendingStatus: "",
    createdAt: "",
  });



  const allParentDomains = useMemo(() => {
    if (!filters) return [];
    return filters.parent_domains || [];
  }, [filters]);

  const allDomains = useMemo(() => {
    if (!filters) return [];
    const domains = [];
    filters.parent_domains?.forEach((pd) => {
      pd.domains?.forEach((d) => {
        domains.push({ ...d, parentDomain: pd.parent_domain });
      });
    });
    return domains;
  }, [filters]);

  const allSubDomains = useMemo(() => {
    if (!filters) return [];
    const subDomains = [];
    filters.parent_domains?.forEach((pd) => {
      pd.domains?.forEach((d) => {
        d.sub_domains?.forEach((sd) => {
          subDomains.push({
            ...sd,
            parentDomain: pd.parent_domain,
            domain: d.domain,
          });
        });
      });
    });
    return subDomains;
  }, [filters]);

  const allProducts = useMemo(() => {
    if (!filters) return [];
    const products = [];
    filters.parent_domains?.forEach((pd) => {
      pd.domains?.forEach((d) => {
        d.sub_domains?.forEach((sd) => {
          sd.products?.forEach((p) => {
            products.push({
              name: p,
              parentDomain: pd.parent_domain,
              domain: d.domain,
              subDomain: sd.sub_domain,
            });
          });
        });
      });
    });
    return products;
  }, [filters]);


  const availableDomains = useMemo(() => {
    let filtered = allDomains;

    if (selectedParentDomain?.value) {
      filtered = filtered.filter(
        (d) => d.parentDomain === selectedParentDomain.value
      );
    }

    if (selectedSubDomain?.value) {
      const sd = allSubDomains.find(
        (s) => s.sub_domain === selectedSubDomain.value
      );
      if (sd) filtered = filtered.filter((d) => d.domain === sd.domain);
    }

    if (selectedProduct?.value) {
      const p = allProducts.find((x) => x.name === selectedProduct.value);
      if (p) filtered = filtered.filter((d) => d.domain === p.domain);
    }

    return filtered;
  }, [
    allDomains,
    allSubDomains,
    allProducts,
    selectedParentDomain,
    selectedSubDomain,
    selectedProduct,
  ]);

  const availableSubDomains = useMemo(() => {
    let filtered = allSubDomains;

    if (selectedParentDomain?.value) {
      filtered = filtered.filter(
        (sd) => sd.parentDomain === selectedParentDomain.value
      );
    }

    if (selectedDomain?.value) {
      filtered = filtered.filter(
        (sd) => sd.domain === selectedDomain.value
      );
    }

    if (selectedProduct?.value) {
      const p = allProducts.find((x) => x.name === selectedProduct.value);
      if (p) filtered = filtered.filter(
        (sd) => sd.sub_domain === p.subDomain
      );
    }

    return filtered;
  }, [
    allSubDomains,
    allProducts,
    selectedParentDomain,
    selectedDomain,
    selectedProduct,
  ]);

  const availableProducts = useMemo(() => {
    let filtered = allProducts;

    if (selectedParentDomain?.value) {
      filtered = filtered.filter(
        (p) => p.parentDomain === selectedParentDomain.value
      );
    }

    if (selectedDomain?.value) {
      filtered = filtered.filter(
        (p) => p.domain === selectedDomain.value
      );
    }

    if (selectedSubDomain?.value) {
      filtered = filtered.filter(
        (p) => p.subDomain === selectedSubDomain.value
      );
    }

    return filtered;
  }, [
    allProducts,
    selectedParentDomain,
    selectedDomain,
    selectedSubDomain,
  ]);


  const handleParentDomainChange = (val) => {
    setSelectedParentDomain(val);
    setSelectedDomain(null);
    setSelectedSubDomain(null);
    setSelectedProduct(null);
  };
  

  const handleDomainChange = (val) => {
    setSelectedDomain(val);
    if (val && !selectedParentDomain) {
      const d = allDomains.find((x) => x.domain === val.value);
      if (d) {
        setSelectedParentDomain({
          label: d.parentDomain,
          value: d.parentDomain,
        });
      }
    }
  };

  const handleSubDomainChange = (val) => {
    setSelectedSubDomain(val);
    if (val) {
      const sd = allSubDomains.find((x) => x.sub_domain === val.value);
      if (sd) {
        if (!selectedParentDomain) {
          setSelectedParentDomain({
            label: sd.parentDomain,
            value: sd.parentDomain,
          });
        }
        if (!selectedDomain) {
          setSelectedDomain({
            label: sd.domain,
            value: sd.domain,
          });
        }
      }
    }
  };

 const handleProductChange = (val) => {
  setSelectedProduct(val);

  // 🟢 If product is cleared, do NOT keep any product-based constraints
  if (!val) {
    return;
  }

  const p = allProducts.find((x) => x.name === val.value);
  if (p) {
    if (!selectedParentDomain) {
      setSelectedParentDomain({
        label: p.parentDomain,
        value: p.parentDomain,
      });
    }
    if (!selectedDomain) {
      setSelectedDomain({
        label: p.domain,
        value: p.domain,
      });
    }
    if (!selectedSubDomain) {
      setSelectedSubDomain({
        label: p.subDomain,
        value: p.subDomain,
      });
    }
  }
};


  const handleCategoryChange = (val) => {
    setSelectedCategory(val);
  };

  // -----------------------------
  // Fetch incidents
  // -----------------------------

  const fetchIncidents = async (page = 1, searchOverride = undefined) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      const effectiveSearch =
        searchOverride !== undefined ? searchOverride : searchValue;

      if (selectedParentDomain?.value) {
        params.append("parent_domain", selectedParentDomain.value);
      }
      if (selectedDomain?.value) {
        params.append("domain", selectedDomain.value);
      }
      if (selectedSubDomain?.value) {
        params.append("sub_domain", selectedSubDomain.value);
      }
      if (selectedProduct?.value) {
        params.append("product", selectedProduct.value);
      }
      if (selectedCategory?.value) {
        params.append("incident_category", selectedCategory.value);
      }
      if (effectiveSearch?.trim()) {
        params.append("snowId", effectiveSearch.trim());
      }
      if (resolutionPendingStatus?.trim()) {
        params.append("resolutionPendingStatus", resolutionPendingStatus);
      }
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);

      const columnMapping = {
        snowId: "snowId",
        incidentDescription: "incidentDescription",
        incidentCategory: "incidentCategory",
        assignedTo: "assignedTo",
        status: "status",
        resolutionPendingStatus: "resolutionPendingStatus",
        createdAt: "createdAt",
      };

      const activeColumnSearch = Object.entries(columnSearch).find(
        ([, v]) => v && v.trim()
      );

      if (activeColumnSearch) {
        const [frontendCol, val] = activeColumnSearch;
        const apiCol = columnMapping[frontendCol];
        if (apiCol) {
          params.append("searchColumn", apiCol);
          params.append("searchValue", val.trim());
        }
      }

      params.append("page", page);
      params.append("page_size", PAGE_SIZE);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-all-incidents?${params.toString()}`
      );

      if (response.data?.status === 200) {
        const normalizedIncidents = (response.data.data || []).map(
          (incident) => ({
            ...incident,
            displayIncidentCategory: formatIncidentCategory(
              incident.incidentCategory
            ),
          })
        );

        const normalizedCategoryCounts = Object.entries(
          response.data.incidentCategoryCounts || {}
        ).reduce((acc, [key, value]) => {
          const label = formatIncidentCategory(key);
          acc[label] = (acc[label] || 0) + value;
          return acc;
        }, {});

        setIncidents(normalizedIncidents);
        setIncidentCategories(response.data.incidentCategories || []);
        setIncidentCategoryCounts(normalizedCategoryCounts);
        setFilters(response.data.filters || null);

        setPagination({
          ...(response.data.pagination || pagination),
          pageSize: PAGE_SIZE,
        });
      }
    } catch (error) {
      console.error("Error fetching incidents:", error);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchIncidents(1);
  }, []);

  useEffect(() => {
    if (filters) fetchIncidents(1);
  }, []);

  useEffect(() => {
    if (!filters) return;
    const id = setTimeout(() => {
      fetchIncidents(1);
    }, 500);
    return () => clearTimeout(id);
  }, [columnSearch]);

  useEffect(() => {
    if (resetTrigger) {
      fetchIncidents(1);
      setResetTrigger(false);
    }
  }, [resetTrigger]);


  const handleApplyFilters = () => {
    fetchIncidents(1);
  };

  const handlePageChange = (newPage) => {
    fetchIncidents(newPage);
  };

  const resetAll = () => {
    setSelectedParentDomain(null);
    setSelectedDomain(null);
    setSelectedSubDomain(null);
    setSelectedProduct(null);
    setSelectedCategory(null);
    setSearchValue("");
    setSortColumn("");
    setSortOrder("asc");
    setStartDate("");
    setEndDate("");
    setResolutionPendingStatus("");
    setColumnSearch({
      snowId: "",
      incidentDescription: "",
      incidentCategory: "",
      assignedTo: "",
      status: "",
      resolutionPendingStatus: "",
      createdAt: "",
    });
    setResetTrigger(true);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const handleColumnSearchChange = (column, value) => {
    if (column !== "resolutionPendingStatus") {
      setResolutionPendingStatus("");
    }

    setColumnSearch({
      snowId: "",
      incidentDescription: "",
      incidentCategory: "",
      assignedTo: "",
      status: "",
      resolutionPendingStatus: "",
      createdAt: "",
      [column]: value,
    });
  };

useEffect(() => {
  if (
    availableDomains.length === 1 &&
    !selectedDomain
  ) {
    const d = availableDomains[0];
    setSelectedDomain({
      label: d.domain,
      value: d.domain,
    });
  }
}, [availableDomains, selectedDomain]);

useEffect(() => {
  const unique = new Map();
  availableSubDomains.forEach((sd) => {
    if (!unique.has(sd.sub_domain)) {
      unique.set(sd.sub_domain, sd);
    }
  });

  const list = Array.from(unique.values());

  if (
    list.length === 1 &&
    !selectedSubDomain
  ) {
    const sd = list[0];
    setSelectedSubDomain({
      label: sd.sub_domain,
      value: sd.sub_domain,
    });
  }
}, [availableSubDomains, selectedSubDomain]);

useEffect(() => {
  const unique = new Map();
  availableProducts.forEach((p) => {
    if (!unique.has(p.name)) {
      unique.set(p.name, p);
    }
  });

  const list = Array.from(unique.values());

  if (
    list.length === 1 &&
    !selectedProduct
  ) {
    const p = list[0];
    setSelectedProduct({
      label: p.name,
      value: p.name,
    });
  }
}, [availableProducts, selectedProduct]);




  if (loading && incidents.length === 0) {
    return (
       <div className="relative bg-white flex items-center justify-center z-50 min-h-screen">
             <div className="flex flex-col items-center space-y-3">
               <img src={heartLoading} alt="Loading..." width={200} />
               <span className="text-gray-700 text-[20px] text-center">
                 Loading incidents...
               </span>
             </div>
           </div>
    );
  }



  return (
    <div className="incident-wrapper bg-[#f9fafb] space-y-4 p-4">
      <div className="flex flex-col pb-4">
        <h1 className="text-[24px] font-bold">Incident Management</h1>
        <div>Find your all incidents</div>
      </div>

      <IncidentPieChart categoryCount={incidentCategoryCounts} />

      <div className="border border-gray-300 rounded-lg">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border transition-colors h-[45px] whitespace-nowrap ${
                  showFilters
                    ? "bg-[#bb1ccc] text-white border-[#bb1ccc]"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                <Filter className="h-4 w-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
              <button
                onClick={resetAll}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-g rounded-lg hover:bg-[#bb1ccc] flex items-center gap-2 h-[45px] whitespace-nowrap"
              >
                <RotateCcw className="h-4 w-4" />
                Reset
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-8 gap-4 pt-4 border-t pb-4 px-4 bg-white shadow">
              <SingleSelect
                label="Parent Domain"
                value={selectedParentDomain}
                options={allParentDomains.map((pd) => ({
                  label: pd.parent_domain,
                  value: pd.parent_domain,
                }))}
                onChange={handleParentDomainChange}
              />

              <SingleSelect
                label="Domain"
                value={selectedDomain}
                options={availableDomains.map((d) => ({
                  label: d.domain,
                  value: d.domain,
                }))}
                onChange={handleDomainChange}
              />

              <SingleSelect
                label="Sub Domain"
                value={selectedSubDomain}
                options={availableSubDomains.map((sd) => ({
                  label: sd.sub_domain,
                  value: sd.sub_domain,
                }))}
                onChange={handleSubDomainChange}
              />

              <SingleSelect
                label="Product"
                value={selectedProduct}
                options={availableProducts.map((p) => ({
                  label: p.name,
                  value: p.name,
                }))}
                onChange={handleProductChange}
              />

              <SingleSelect
                label="Incident Category"
                value={selectedCategory}
                options={incidentCategories.map((c) => ({
                  label: formatIncidentCategory(c),
                  value: c,
                }))}
                onChange={handleCategoryChange}
              />

              <div>
                <label className="block text-[14px] font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 h-[35px] border rounded-lg"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-[14px] font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 h-[35px] border rounded-lg"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={handleApplyFilters}
                  className="px-6 py-2.5 text-sm font-medium text-white bg-[#bb1ccc] rounded-lg min-h-[35px]"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          )}
        </div>

        <IncidentTable
          loading={loading}
          incidents={incidents}
          selectedParentDomain={selectedParentDomain?.value || ""}
          selectedDomain={selectedDomain?.value || ""}
          selectedSubDomain={selectedSubDomain?.value || ""}
          selectedProduct={selectedProduct?.value || ""}
          searchValue={searchValue}
          selectedCategory={selectedCategory?.value || ""}
          resetAll={resetAll}
          pagination={pagination}
          handlePageChange={handlePageChange}
          PAGE_SIZE={PAGE_SIZE}
          sortColumn={sortColumn}
          sortOrder={sortOrder}
          handleSort={handleSort}
          columnSearch={columnSearch}
          onColumnSearchChange={handleColumnSearchChange}
          setResolutionPendingStatus={setResolutionPendingStatus}
        />
      </div>
    </div>
  );
};

export default ActionTable;
